/** Émotions autorisées pour les personnages (aligné BSM / moteur). */
export const ALLOWED_EMOTIONS = [
  'neutre',
  'happy',
  'angry',
  'surprised',
  'sad',
  'suspicious',
  'worried',
  'waving',
  'laughing',
] as const;

export type AllowedEmotion = (typeof ALLOWED_EMOTIONS)[number];

/** Anciennes clés FR / BSM → clés canoniques de l'éditeur. */
export const LEGACY_EMOTION_MAP: Record<string, AllowedEmotion> = {
  heureuse: 'happy',
  heureux: 'happy',
  souriant: 'happy',
  souriante: 'happy',
  riant: 'laughing',
  riante: 'laughing',
  triste: 'sad',
  soupconneux: 'suspicious',
  soupconneuse: 'suspicious',
  inquiet: 'worried',
  inquiete: 'worried',
  salut: 'waving',
  pensif: 'worried',
  pensive: 'worried',
  surpris: 'surprised',
  surprisee: 'surprised',
  'en colere': 'angry',
  colere: 'angry',
  enerve: 'angry',
  enervee: 'angry',
  énervé: 'angry',
  serieux: 'neutre',
  desinvolte: 'happy',
  moqueur: 'laughing',
  moqueuse: 'laughing',
};

export function isAllowedEmotion(value: string): value is AllowedEmotion {
  return (ALLOWED_EMOTIONS as readonly string[]).includes(value);
}

/** Mappe une clé legacy ou canonique vers une clé autorisée, ou null si inconnue. */
export function normalizeEmotionKey(value: string): AllowedEmotion | null {
  if (isAllowedEmotion(value)) return value;
  const lower = value.toLowerCase().trim();
  if (isAllowedEmotion(lower)) return lower;
  return LEGACY_EMOTION_MAP[lower] ?? LEGACY_EMOTION_MAP[value] ?? null;
}

/**
 * Normalise un dictionnaire d'émotions : clés canoniques uniquement.
 * Les clés canoniques existantes priment sur les alias legacy.
 */
export function normalizeEmotionsRecord(
  emotions?: Record<string, string>
): Record<string, string> {
  if (!emotions) return {};
  const result: Record<string, string> = {};

  for (const [key, path] of Object.entries(emotions)) {
    if (!path) continue;
    if (isAllowedEmotion(key)) continue;
    const norm = normalizeEmotionKey(key);
    if (norm && !(norm in result)) result[norm] = path;
  }

  for (const key of ALLOWED_EMOTIONS) {
    if (emotions[key]) result[key] = emotions[key];
  }

  return result;
}

/** Clés d'émotions définies sur le personnage, dans l'ordre canonique. */
export function listCharacterEmotionKeys(emotions?: Record<string, string>): string[] {
  const normalized = normalizeEmotionsRecord(emotions);
  return ALLOWED_EMOTIONS.filter((e) => e in normalized);
}

/** Émotions avec un asset renseigné (pour les dialogues). */
export function listConfiguredEmotions(emotions?: Record<string, string>): string[] {
  const normalized = normalizeEmotionsRecord(emotions);
  return ALLOWED_EMOTIONS.filter((e) => Boolean(normalized[e]));
}
